import { Checkbox } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import {
  ApiButton,
  Button,
  Chip,
  CustomAutocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ErrorInfo,
  Typography,
  useSnackbarHandler,
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import IconWithLabel from 'web-library/stories/atom/IconWithLabel/IconWithLabel';
import {
  DropdownRequestType,
  useGetDropdownRequestTypes,
} from '../../../../api-refactored/candidate/useGetDropdownRequestTypes';
import { usePostIexamRequestType } from '../../../../api-refactored/candidate/usePostIexamRequestType';
import { usePutEnableRequestType } from '../../../../api-refactored/candidate/usePutEnableRequestType';
import {
  GET_DROPDOWN_REQUEST_TYPES_QUERY_KEY,
  GET_ENABLED_REQUEST_TYPES_QUERY_KEY,
} from '../../../../constants/queryKeys';
import { useStyles } from './style';

interface AddRequestTypeModalProps {
  modalState: boolean;
  setModalState: React.Dispatch<React.SetStateAction<boolean>>;
}

const AddRequestTypeModal = ({ modalState, setModalState }: AddRequestTypeModalProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { snackbarHandler } = useSnackbarHandler();
  const queryClient = useQueryClient();
  const [mutateError, setMutateError] = useState<string>();
  const [selectedMoa, setSelectedMoa] = useState<string | null>(null);
  const [selectedAAType, setSelectedAAType] = useState<DropdownRequestType[]>([]);

  const addRequestType = usePutEnableRequestType({
    onSuccess: () => {
      queryClient.invalidateQueries(GET_DROPDOWN_REQUEST_TYPES_QUERY_KEY);
      queryClient.invalidateQueries(GET_ENABLED_REQUEST_TYPES_QUERY_KEY);
      setSelectedMoa(null);
      setSelectedAAType([]);
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('admin->>AccessArrangementConfiguration.RequestTypeListUpdated'),
      });
    },
    onError: () => {
      setMutateError(t('admin->>AccessArrangementConfiguration.UnableToAddRequestType'));
    },
  });

  const iexamRequestType = usePostIexamRequestType({
    onSuccess: () => {
      queryClient.invalidateQueries(GET_DROPDOWN_REQUEST_TYPES_QUERY_KEY);
      // to reset the selection cos data might updated
      setSelectedAAType([]);
      setSelectedMoa(null);
    },
  });

  const {
    data: dropdownData,
    isLoading: isDropdownDataLoading,
    error: isDropdownDataError,
    refetch: refetchDropdownData,
  } = useGetDropdownRequestTypes();

  const onDialogClose = () => {
    setModalState(false);
    setSelectedMoa(null);
    setSelectedAAType([]);
    iexamRequestType.reset();
    addRequestType.reset();
    setMutateError(undefined);
  };

  const onSubmitConfirm = () => {
    setMutateError(undefined);
    addRequestType.mutate(
      selectedAAType.map((option: DropdownRequestType) => ({
        uuid: option.uuidOfType,
      }))
    );
  };

  return (
    <Dialog open={modalState} maxWidth="md">
      <DialogTitle>
        {t('admin->>AccessArrangementConfiguration.AddAccessArrangementType')}
      </DialogTitle>

      <DialogContent>
        <Typography className={classes.retrieveInstruction}>
          {t('admin->>AccessArrangementConfiguration.RetrieveRequestTypesInstruction')}
        </Typography>
        <ApiButton
          className={classes.retrieveButton}
          onClick={() => {
            iexamRequestType.mutate();
          }}
          status={iexamRequestType.status}
          variant="outlined"
          successLabel={t('admin->>AccessArrangementConfiguration.RequestTypeUpdated')}
          id="button-retrieve-latest-request-type"
        >
          {t('admin->>AccessArrangementConfiguration.RetrieveLatestRequestType')}
        </ApiButton>

        <CustomAutocomplete
          onChange={(event, value) => {
            setSelectedMoa(value as string);
            setSelectedAAType([]);
          }}
          options={dropdownData ? Object.keys(dropdownData) : []}
          placeholder={t('admin->>AccessArrangementConfiguration.SelectModeOfAssessment')}
          value={selectedMoa}
          label={t('admin->>AccessArrangementConfiguration.ModeOfAssessment')}
          loading={isDropdownDataLoading}
          loadingError={!!isDropdownDataError}
          loadingErrorOnReFetch={refetchDropdownData}
          disabled={addRequestType.isLoading}
          id="select-moa"
        />
        {selectedMoa && (
          <CustomAutocomplete<DropdownRequestType, true, false, undefined>
            className={classes.aaTypeSelectDropdown}
            onChange={(event, value) => setSelectedAAType(value as DropdownRequestType[])}
            options={dropdownData ? dropdownData[selectedMoa] : []}
            placeholder={t('admin->>AccessArrangementConfiguration.SelectRequestType')}
            value={selectedAAType}
            label={t('admin->>AccessArrangementConfiguration.AccessArrangementType')}
            loading={isDropdownDataLoading}
            loadingError={!!isDropdownDataError}
            loadingErrorOnReFetch={refetchDropdownData}
            disabled={addRequestType.isLoading}
            getOptionLabel={(option) => option.accessArrangementType}
            isOptionEqualToValue={(option, value) => option.uuidOfType === value.uuidOfType}
            multiple
            disableCloseOnSelect
            id="select-aa-type"
            renderOption={(_,option,{selected},__)  => (
              <>
                <Checkbox checked={selected} />
                {option.accessArrangementType}
              </>
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, tagIndex) => (
                <Chip
                  id="auto-complete"
                  label={option.accessArrangementType}
                  {...getTagProps({ index: tagIndex })}
                />
              ))
            }
          />
        )}
        {mutateError && <ErrorInfo label={mutateError} />}
      </DialogContent>

      <DialogActions>
        <div className={classes.dialogButtonContainer}>
          <Button type="button" variant="text" id="button-cancel" onClick={() => onDialogClose()}>
            {t('admin->>AccessArrangementConfiguration.Cancel')}
          </Button>
          <ApiButton
            type="submit"
            id="button-add-request-type"
            onClick={() => {
              onSubmitConfirm();
            }}
            status={addRequestType.status}
            disabled={selectedAAType.length === 0}
            successLabel={t('admin->>AccessArrangementConfiguration.AddedToList')}
          >
            <IconWithLabel
              iconSize="M"
              iconName={IconName.add}
              label={t('admin->>AccessArrangementConfiguration.AddRequestTypeButton', {
                number: selectedAAType.length > 0 ? `${selectedAAType.length} ` : '',
              })}
              typographyProps={{
                variant: 'h5',
              }}
            />
          </ApiButton>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default AddRequestTypeModal;
